import { useState } from "react";
import { BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/15";
const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

function Pills({ options, value, onChange, allLabel, testid, render = (o) => o }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      <button type="button" onClick={() => onChange([])} className={`rounded-full px-3 py-1 text-xs font-medium transition ${value.length === 0 ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"}`} data-testid={`${testid}-all`}>{allLabel}</button>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.id;
        const on = value.includes(v);
        return <button key={v} type="button" onClick={() => onChange(toggle(value, v))} data-testid={`${testid}-${v}`}
          className={`rounded-full px-3 py-1 text-xs font-medium transition ${on ? "bg-primary text-white" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"}`}>{render(o)}</button>;
      })}
    </div>
  );
}

/** Create / edit a district subject and choose which schools + grades it deploys to. */
export function SubjectTargetDialog({ subject, schools, gradeOptions, onClose, onSaved }) {
  const [f, setF] = useState({ name: subject?.name || "", school_ids: subject?.school_ids || [], grades: subject?.grades || [] });
  const [customGrade, setCustomGrade] = useState("");
  const [busy, setBusy] = useState(false);
  const grades = [...new Set([...gradeOptions, ...f.grades])];

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = subject?.id ? await api.put(`/corporate/academics/subjects/${subject.id}`, f) : await api.post("/corporate/academics/subjects", f);
      toast.success(`${data.name} saved`); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="subject-target-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><BookOpen className="h-5 w-5 text-primary" /> {subject?.id ? "Edit subject" : "New district subject"}</DialogTitle>
          <DialogDescription>Choose where this subject is deployed. Leave "All" selected to send it everywhere.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <label className="block"><span className="block mb-1 text-[11px] uppercase tracking-widest text-slate-500">Subject name</span>
            <input required autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Algebra I" data-testid="subject-target-name" className={inputCls} /></label>
          <div><span className="block mb-1.5 text-[11px] uppercase tracking-widest text-slate-500">Schools</span>
            <Pills options={schools} value={f.school_ids} onChange={(v) => setF({ ...f, school_ids: v })} allLabel="All schools" testid="subject-target-school" render={(s) => s.name} /></div>
          <div><span className="block mb-1.5 text-[11px] uppercase tracking-widest text-slate-500">Grades</span>
            <Pills options={grades} value={f.grades} onChange={(v) => setF({ ...f, grades: v })} allLabel="All grades" testid="subject-target-grade" />
            <div className="mt-2 flex gap-2">
              <input value={customGrade} onChange={(e) => setCustomGrade(e.target.value)} placeholder="Add a grade (e.g. 9)" data-testid="subject-target-grade-input" className={`${inputCls} h-9 max-w-[200px]`} />
              <button type="button" disabled={!customGrade.trim()} onClick={() => { setF({ ...f, grades: [...new Set([...f.grades, customGrade.trim()])] }); setCustomGrade(""); }} data-testid="subject-target-grade-add"
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40">Add grade</button>
            </div>
          </div>
          <DialogFooter>
            <button type="submit" disabled={busy} data-testid="subject-target-save" className="h-10 rounded-lg bg-primary px-5 text-sm font-medium text-white inline-flex items-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {subject?.id ? "Save" : "Add subject"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
